import React, { forwardRef } from "react";
import styled from "styled-components";
import IEditableProps from "../../utilities/IEditableProps";
import IStyledProps from "../../utilities/IStyledProps";
import classNames from "classnames";
import { parseHTML } from "../../utilities/markdown";
import GalleryItem from "./GalleryItem";

export interface INewsroomGalleryProps extends IEditableProps, IStyledProps {
    mediaSrc?: string;
    mediaAlt?: string;
    header: string;
    imageTitle: string;
    videoTitle: string;
    audioTitle: string;
    docTitle: string;
    children: React.ReactNode;
}

interface MediaFile {
    src: string;
    alt: string;
}

const NewsroomGallery = forwardRef((props: INewsroomGalleryProps, ref: any) => {
    const { className, editorClassObj, cmsId, mediaSrc, mediaAlt } = props;
    const mediaSections = [
        { name: "image", useThumbnail: true, ext: [".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg"], text: props.imageTitle, fallback: "/media/newsroom/image-file.svg" },
        { name: "video", useThumbnail: true, ext: [".mp4"], text: props.videoTitle, fallback: "/media/newsroom/video-file.svg" },
        { name: "audio", useThumbnail: false, ext: [".mp3"], text: props.audioTitle, fallback: "/media/newsroom/audio-file.svg" },
        { name: "doc", useThumbnail: false, ext: [".pdf"], text: props.docTitle, fallback: "/media/newsroom/doc-file.svg" },
    ];
    function getMedia(): MediaFile[] {
        try {
            const fileList = mediaSrc ? JSON.parse(mediaSrc) : [];
            const altList = mediaAlt ? JSON.parse(mediaAlt) : [];
            return fileList
                .filter((src: string) => Boolean(src))
                .map((src: string, index: number) => ({ src, alt: altList[index] }));
        } catch (e) {
            console.error("Newsroom Gallery failed to parse media files", e);
            return [];
        }
    }
    function getMediaByExtension(files: MediaFile[], extensions: string[]) {
        return files.filter((file) => extensions.some((ext) => file.src.toLowerCase().endsWith(ext)));
    }
    const mediaList = getMedia();

    if (mediaList.length === 0) {
        console.warn("Newsroom Gallery empty, widget not rendered");
        if (cmsId) {
            // Show newsroom gallery empty placeholder in Buildr preview only
            return (
                <div data-id={cmsId} ref={ref} className={classNames("newsroom-gallery", "content", className, editorClassObj)}>
                    <div className="modal-placeholder p-3 text-primary">Empty Media Gallery</div>
                </div>
            );
        }
        return null;
    }

    return (
        <div data-id={cmsId} ref={ref} className={classNames("newsroom-gallery", "content", className, editorClassObj)}>
            <div className="gallery-header">{props.children}</div>
            {mediaSections.map((section: any) => {
                const media = getMediaByExtension(mediaList, section.ext);
                if (media.length === 0) {
                    return null;
                }
                return <section key={section.name}>
                    <h4 className="mt-5">{parseHTML(section.text, 'Newsroom Section Title')}</h4>
                    <hr/>

                    <div className="row mb-6">
                        {media.map((mediaFile: MediaFile) => {
                            const thumbnailSrc = mediaFile.src.replace(/(\.[^.]+)$/, "-thumbnail.webp");
                            return <GalleryItem 
                                loading='lazy' 
                                key={mediaFile.src} 
                                cmsId={cmsId} 
                                src={mediaFile.src} 
                                thumbnailSrc={section.useThumbnail ? thumbnailSrc : section.fallback} 
                                fallbackSrc={section.fallback} 
                                alt={mediaFile.alt} 
                                dataTestId=""
                            />
                        })}
                    </div>
                </section>
            })}
        </div>
    );
});

export default styled(NewsroomGallery)`
    .downloadIcon {
        position: absolute;
        right: 20px;
        bottom: 0px;
        width: 14px;
        cursor: pointer;
    }

    .fileFormat {
        font-size: 12px;
        color: var(--mud);
    }
`;

(global as any).NewsroomGallery = NewsroomGallery;